import type {
  LifeRecord,
  EarthRow,
  EarthDivergence,
  LifeCareerStage,
  LifeFuturePath,
  LifeTimelineEntry,
  LifeBeat,
  EarthDimension,
} from "../../types";
import type { NarrativeContext, NarrativeResult } from "../../narrative";
import { ENGINE_VERSION } from "../../lifeChapters";
import { lifeStream, ageFor, clampLevel, pick, pickDistinct, listJoin, cap } from "./lifeHelpers";
import { DIVERGENT_HISTORICAL_FORKS, DIVERGENT_ROUTINE_TEMPLATES } from "./divergent.life.banks";

/**
 * Builds a rich, fully populated LifeRecord for The Divergent Self.
 * Follows Earth physics and a recognizable human society with ONE concrete historical fork,
 * matching every requirement and count in docs/wvc-batch-spec.md.
 */
export function buildDivergentLife(ctx: NarrativeContext, base: NarrativeResult): LifeRecord {
  // Independent seeded RNGs under "life.*"
  const ageRng = lifeStream(ctx, "divergent.age");
  const forkRng = lifeStream(ctx, "divergent.fork");
  const routineRng = lifeStream(ctx, "divergent.routine");
  const beatRng = lifeStream(ctx, "divergent.beats");
  const futureRng = lifeStream(ctx, "divergent.futures");
  const bioRng = lifeStream(ctx, "divergent.biography");
  const homeRng = lifeStream(ctx, "divergent.home");
  const peopleRng = lifeStream(ctx, "divergent.people");
  const standingRng = lifeStream(ctx, "divergent.standing");
  const workRng = lifeStream(ctx, "divergent.work");
  const formativeRng = lifeStream(ctx, "divergent.formative");

  // 1. Age: integer 34..54 (satisfies 19..84)
  const age = ageFor(ageRng, 34, 54);
  const influenceScore = clampLevel(ctx.axes.influence, 30, 92);

  // 2. Concrete Historical Fork (out of 10 rich forks)
  const primaryFork = pick(DIVERGENT_HISTORICAL_FORKS, forkRng);

  // Pick secondary forks for EarthDivergences (3-4 total, IDs "d1".."d4")
  const otherForks = DIVERGENT_HISTORICAL_FORKS.filter((f) => f.id !== primaryFork.id);
  const pickedOthers = pickDistinct(otherForks, 3, forkRng);

  const divergences: EarthDivergence[] = [
    {
      id: "d1",
      title: primaryFork.title,
      text: primaryFork.divergenceText,
      effects: primaryFork.divergenceEffects,
    },
    {
      id: "d2",
      title: pickedOthers[0].title,
      text: pickedOthers[0].divergenceText,
      effects: pickedOthers[0].divergenceEffects,
    },
    {
      id: "d3",
      title: pickedOthers[1].title,
      text: pickedOthers[1].divergenceText,
      effects: pickedOthers[1].divergenceEffects,
    },
    {
      id: "d4",
      title: pickedOthers[2].title,
      text: pickedOthers[2].divergenceText,
      effects: pickedOthers[2].divergenceEffects,
    },
  ];

  // 3. Relationships and occupation continuity
  const rel1 = base.dossier.relationships[0]?.who || "Elena Vance";
  const rel2 = base.dossier.relationships[1]?.who || "Marcus Thorne";
  const rel3 = base.dossier.relationships[2]?.who || "Sora Chen";
  const occupation = base.occupation;
  const title = base.title;
  const name = base.name;

  // 4. Identity & Biography (4-5 paragraphs, each 2-4 sentences)
  const townOrRegion = base.worldDetails[0]?.value || "the regional valley";
  const primaryTurning = base.story.turningPoint;
  const earlyOrigin = base.story.origin;

  const bioParas: string[] = [
    `${name} was born into a grounded provincial community where practical stewardship and intergenerational memory form the bedrock of daily life. Early family experiences instilled a quiet confidence in physical materials and an abiding respect for honest, unhurried handiwork. Rather than chasing distant credentials, ${name} chose to learn from seasoned regional practitioners who measured true accomplishment by durability rather than speculative haste.`,

    `During their early twenties, a critical juncture tested that resolve when ${(primaryTurning ?? "").slice(0, 160)}. Refusing the temptation of distant metropolitan abstractions, ${name} dedicated themselves entirely to mastering ${occupation.toLowerCase()}, absorbing the intricate mechanical and civic nuances of the trade. That deliberate choice cemented their reputation among neighbors as someone whose commitments could be trusted without hesitation.`,

    `Over the following decades, ${name} emerged as ${title}, guiding vital regional projects and mentoring successive cohorts of apprentices. Their daily practice bridges exacting technical precision with deep communal empathy, proving that mastering one locality provides a deeper foundation than skimming across continents. Colleagues throughout the district frequently seek ${name}'s counsel whenever delicate negotiations or complex physical repairs demand a steady, experienced hand.`,

    `Today, living in a solid home along the district waterways, ${name} maintains an unhurried, purposeful cadence centered on active public stewardship. Surrounded by trusted companions like ${rel1} and ${rel3}, they demonstrate that a life rooted in honest labor, enduring friendships, and local care leaves an indelible, peaceful mark on the world. Even when contentious regional debates emerge with civic delegates like ${rel2}, ${name}'s moral spine and patient clarity ensure that the common welfare remains protected.`,
  ];

  // Add 5th paragraph if bioRng indicates, ensuring 4-5 paragraphs
  if (bioRng() > 0.5) {
    bioParas.push(
      `Looking toward the horizon, ${name} remains committed to codifying their decades of practical wisdom into open guild treatises so future generations can maintain these vital infrastructure systems without external reliance. In an era where global noise often drowns out quiet competence, this life stands as a testament to the profound dignity of staying put and doing one thing exquisitely well.`
    );
  }

  // 5. Work & Career History (4 stages, 5-6 responsibilities, 4-6 expertise)
  const careerHistory: LifeCareerStage[] = [
    {
      stage: "Foundational Apprenticeship",
      years: `Ages 16–22`,
      text: `Mastered the foundational tools, measurement standards, and material properties of the trade under veteran guild supervision, learning to diagnose systemic faults by ear and touch.`,
    },
    {
      stage: "Journeyman Practitioner",
      years: `Ages 23–31`,
      text: `Operated independently across neighboring districts, completing demanding municipal restoration contracts and establishing a personal reputation for meticulous reliability.`,
    },
    {
      stage: "Senior Guild Master",
      years: `Ages 32–${age - 3}`,
      text: `Assumed leadership of the central district workshop, directing complex public infrastructure upgrades and formalizing apprentice pedagogy for a new generation.`,
    },
    {
      stage: "Provincial Trustee & Advisor",
      years: `Age ${age - 2}–Present`,
      text: `Currently guides regional planning boards on resource conservation while maintaining an active personal atelier bench and consulting on emergency repairs.`,
    },
  ];

  const responsibilities: string[] = [
    `Directing technical diagnostics, repair schedules, and quality assurance for all ${occupation.toLowerCase()} operations across the municipal ward.`,
    `Mentoring and evaluating advanced apprentices, ensuring rigorous mastery of open-hardware schematics and physical safety codes.`,
    `Arbitrating material allocations and supply-chain logistics during seasonal river freezes or resource shortages.`,
    `Collaborating with municipal assemblies to plan twenty-year capital infrastructure investments without incurring speculative debt.`,
    `Inspecting public mechanical conduits, water weirs, and cooperative workshops to guarantee long-term operational resilience.`,
    `Maintaining detailed archival logs of local equipment failures and restorative modifications for public study.`,
  ];

  const expertise: string[] = [
    `Regional infrastructure diagnostics and stress analysis`,
    `Precision hand-tool fabrication and mechanical calibration`,
    `Civic guild consensus mediation and labor coordination`,
    `Materials conservation and metallurgical stress testing`,
    `Hydrological microclimate analysis and seasonal risk mitigation`,
  ];

  const workplaceOptions = [
    `A robust timber-and-brick workshop along the central canal district, featuring heavy iron lathes, drafting tables covered in linen blueprints, and tall clerestory windows opening onto the water.`,
    `An airy stone atelier adjoining the municipal rail depot, filled with calibrated mechanical instruments, fragrant cedar storage cabinets, and an open courtyard for heavy casting assemblies.`,
    `A high-ceilinged riverbank forge and diagnostic lab outfitted with water-driven belt machinery, slate drafting boards, and rows of meticulously maintained heirloom hand tools.`,
  ];
  const workplace = pick(workplaceOptions, workRng);

  // 6. Standing & Influence (wealthBracket short, socialClass short, influenceLevel 0..100)
  const wealthBrackets = ["Comfortably Stable", "Debt-Free Cooperative Equity", "Secure Artisan Equity", "Resilient Moderate"];
  const socialClasses = ["Master Craftsman & Civic Trustee", "Chartered Guild Councilor", "Respected Municipal Master", "Senior Guild Warden"];
  const wealthBracket = pick(wealthBrackets, standingRng);
  const socialClass = pick(socialClasses, standingRng);

  // 7. Home & Routine (routine: 6 strings "HH:MM — text", possessions: 4-6)
  const routine = pick(DIVERGENT_ROUTINE_TEMPLATES, routineRng);
  const possessions: string[] = [
    `Inherited brass master vernier calipers stamped with the provincial guild benchmark`,
    `A thumbed, oil-stained series of leather-bound engineering logbooks spanning twenty-five years`,
    `A custom-fabricated mechanical road cycle with heavy panniers for district field inspections`,
    `A hand-tuned acoustic viola crafted by a neighboring instrument maker`,
    `A restored cast-iron woodstove that heats both the domestic kitchen and the drafting annex`,
  ];

  const dwellingOptions = [
    `A two-story fieldstone and timber residence with an attached workshop and a walled pear garden overlooking the canal.`,
    `A quiet cedar-shingled cottage on the town terrace, featuring deep eaves, a slate roof, and a sunlit upper drafting study.`,
    `A converted watermill dwelling along the rushing creek, equipped with a modest mechanical dynamo and stone hearth.`,
  ];
  const dwelling = pick(dwellingOptions, homeRng);

  // 8. People (family, relationships, friendships, socialCircle)
  const people = {
    family: `Maintains affectionate, unhurried ties with extended kin scattered across neighboring market townships, gathering regularly for seasonal harvest dinners and family building raisings.`,
    relationships: `Shares a steadfast domestic and working companionship with ${rel1}, whose pragmatic counsel anchors the household and balances ${name}'s exacting technical focus.`,
    friendships: `A trusted circle of longtime companions, including the municipal delegate ${rel2} and the logistics lead ${rel3}, who meet weekly over dark ale to discuss town affairs with warm candor.`,
    socialCircle: `Firmly woven into the district craft association, the local volunteer fire brigade, and the neighborhood market committee.`,
  };

  // 9. Formative (decisions: 3-4, achievements: 3, setbacks: 2-3, turningPoints: 2-3)
  const decisions: string[] = [
    base.choices[0]?.choice || `Choosing an intensive local guild apprenticeship over a distant corporate scholarship at age twenty.`,
    base.choices[1]?.choice || `Purchasing the derelict municipal foundry shares to keep precision equipment in community hands.`,
    base.choices[2]?.choice || `Refusing a lucrative metropolitan consulting buyout to protect the valley's cooperative water rights.`,
    `Allocating fifty percent of annual workshop earnings into an apprentice emergency mutual-aid fund.`,
  ];

  const achievements: string[] = [
    base.dossier.achievements[0] || `Successfully engineered the floodgate diversion that saved the lower township from a 50-year seasonal crest.`,
    base.dossier.achievements[1] || `Authored the regional standard handbook for apprentice machine safety, adopted across four provinces.`,
    `Restored the historic municipal clock and tidal bells, which have chimed without interruption for over a decade.`,
  ];

  const setbacks: string[] = [
    base.dossier.failures[0] || `Suffered a devastating warehouse fire in their late twenties that destroyed two years of hand-drafted survey maps.`,
    base.dossier.failures[1] || `Experienced an acrimonious split with a former partner over whether to accept state industrial subsidies.`,
  ];

  const turningPoints: string[] = [
    base.story.turningPoint || `The decisive evening when an offer to relocate to the capital was declined in favor of anchoring at home.`,
    `The historic municipal assembly where the cooperative workshop charter was officially ratified by unanimous vote.`,
  ];

  // 10. HowDiffers (headline, paragraphs: 3, keyDifferences: 4-5)
  const howDiffers = {
    headline: primaryFork.howDiffersHeadline,
    paragraphs: primaryFork.howDiffersParagraphs,
    keyDifferences: primaryFork.keyDifferences,
  };

  // 11. Day in Life (title: exactly "A Day in This Life", beats: 8, closing: 1-2 sentences)
  // Must mention relationships (rel1, rel2, rel3) and occupation
  const beats: LifeBeat[] = [
    {
      time: "05:30",
      heading: "First Light on the Water",
      text: `Awakens in the quiet fieldstone dwelling before the town stirs, checking the brass weather gauges on the river porch. Over bitter black tea and fresh brown bread, ${name} reviews the day's priority ledger while the valley mist gradually lifts from the slate roofs.`,
    },
    {
      time: "07:15",
      heading: "Workshop Gates & Morning Delegation",
      text: `Unlocks the heavy timber doors of the municipal atelier as the bells strike quarter past seven. Greets the junior apprentices and discusses the day's casting queue with ${rel1}, who has already laid out the diagnostic calipers and oilcans.`,
    },
    {
      time: "09:45",
      heading: "Hands-On Diagnostics as " + occupation,
      text: `Immerses directly in demanding physical labor, troubleshooting a stubborn mechanical pump assembly that serves the lower ward. The work requires measuring tolerances to within a fraction of a millimeter, testing both patience and seasoned touch.`,
    },
    {
      time: "12:30",
      heading: "Midday Repast & Municipal Debate",
      text: `Walks to the station square canteen to share bread, sharp cheddar, and hot broth with ${rel2}. While the conversation turns heated over proposed budget allocations for next season's repairs, their mutual dedication to the town's welfare keeps the argument respectful and productive.`,
    },
    {
      time: "14:15",
      heading: "Apprentice Instruction & Calibration",
      text: `Returns to the workshop to guide two third-year students through complex metallurgical joinery. ${name} demonstrates the subtle difference in hammer bounce that signals proper temper, reinforcing that real craft cannot be rushed by artificial schedules.`,
    },
    {
      time: "16:45",
      heading: "Civic Logistics Consultation",
      text: `Meets with ${rel3} along the freight docks to inspect an incoming shipment of seasoned timber and brass fittings. Together, they cross-reference bills of lading and verify that every lot complies with the regional cooperative's fair-trade charter.`,
    },
    {
      time: "19:00",
      heading: "Closing the Atelier",
      text: `Secures the iron lathes, cleans the workbench surfaces with linseed oil, and pulls down the heavy canvas dust covers. Takes a quiet moment in the twilight to admire the day's completed repairs before locking the gate for the night.`,
    },
    {
      time: "21:15",
      heading: "Hearthside Rest & Evening Ledger",
      text: `Enjoys a warm family supper with ${rel1} by the glowing ceramic tile stove, discussing neighborhood news and listening to classical music on the wooden radio. Before turning in, ${name} notes the day's accomplishments in the calfskin journal, deeply contented with honest physical effort.`,
    },
  ];

  const closing = `As river reflections shimmer against the ancient bridge piers and the bells strike ten, sleep comes swiftly, accompanied by the deep serenity of another day spent leaving the community sturdier than before.`;

  // 12. Futures (title: "Three Possible Futures", exactly 3 paths a,b,c with flavors, 3 opportunities, 3 challenges each, disclaimer)
  const futures: LifeFuturePath[] = [
    {
      id: "a",
      name: "Provincial Guildmaster",
      flavor: "Most natural",
      summary: `A gradual elevation to coordinate technical standards and apprentice certification across all eight townships in the river basin.`,
      opportunities: [
        `Institutionalizing durable open-hardware standards across the entire province`,
        `Expanding fully subsidized guild apprenticeships for rural working youth`,
        `Authoring the definitive reference treatise on regional mechanical infrastructure`,
      ],
      challenges: [
        `Navigating mounting administrative paperwork that pulls time away from hands-on craft`,
        `Mediating jurisdictional turf wars between conservative upland lodges and coastal ports`,
        `Resisting creeping commercial pressures to accelerate repair schedules at the cost of safety`,
      ],
      consequences: `Earns generational veneration as the steady architect of regional craft permanence, though sacrifices personal bench time.`,
    },
    {
      id: "b",
      name: "Municipal Energy Modernizer",
      flavor: "Ambitious",
      summary: `Spearheading a major multi-district initiative to link independent workshops into an integrated solar and low-head hydro microgrid.`,
      opportunities: [
        `Tripling the energy independence of the entire valley through clean local power`,
        `Securing permanent municipal ownership of all electrical transmission lines`,
        `Demonstrating a replicable model of carbon-negative municipal industrial development`,
      ],
      challenges: [
        `Overcoming entrenched skepticism from elder guild members resistant to electrical shifts`,
        `Managing substantial capital outlays during the three-year turbine construction phase`,
        `Shouldering personal public accountability if unseasonal droughts impact water levels`,
      ],
      consequences: `Transforms the valley into a thriving regional beacon of ecological manufacturing, establishing a lasting legacy across the continent.`,
    },
    {
      id: "c",
      name: "Frontier Agricultural Pioneer",
      flavor: "Wildcard",
      summary: `Relocating to an untamed perimeter basin to build a self-sustaining cooperative settlement and water catchment system from scratch.`,
      opportunities: [
        `Total operational and creative freedom untethered from established town council bylaws`,
        `Pioneering novel permaculture hydraulics and off-grid timber engineering`,
        `Creating a fresh community refuge for young families seeking grounded living`,
      ],
      challenges: [
        `Unforgiving exposure to harsh winter storms and sparse initial supply lines`,
        `Significant physical exertion in remote terrain without established municipal backup`,
        `Enduring prolonged separation from longtime friends and familiar town comforts`,
      ],
      consequences: `Etches an enduring, pioneer legacy into wild geography, creating a vibrant sanctuary that flourishes for generations.`,
    },
  ];

  // 13. Timeline (9-10 entries, ascending age, covering origin, education, choice, achievement, setback, turning, present, future; present = age; future > age)
  const presentAge = age;
  const timeline: LifeTimelineEntry[] = [
    {
      age: 7,
      label: "Age 7",
      title: "Childhood by the Water",
      text: (earlyOrigin ?? "").slice(0, 150) + "...",
      kind: "origin",
    },
    {
      age: 16,
      label: "Age 16",
      title: "Apprenticeship Entry",
      text: `Signed the seven-year guild indenture, committing to master precision measurement and workshop mechanics.`,
      kind: "education",
    },
    {
      age: 21,
      label: "Age 21",
      title: "The Crossroads Choice",
      text: base.choices[0]?.choice || `Turned down an overseas corporate scholarship to stay rooted in the home valley.`,
      kind: "choice",
    },
    {
      age: 27,
      label: "Age 27",
      title: "Flood Defense Triumph",
      text: `Engineered emergency weir reinforcements that prevented catastrophic inundation across the lower market district.`,
      kind: "achievement",
    },
    {
      age: 30,
      label: "Age 30",
      title: "Workshop Loss & Rebuilding",
      text: base.dossier.failures[0] || `Rebuilt the riverside forge following a severe winter freeze that cracked the primary hydraulic flume.`,
      kind: "setback",
    },
    {
      age: 33,
      label: "Age 33",
      title: "Decisive Institutional Pivot",
      text: (base.story?.turningPoint ?? "").slice(0, 150) + "...",
      kind: "turning",
    },
    {
      age: Math.max(34, presentAge - 4),
      label: `Age ${Math.max(34, presentAge - 4)}`,
      title: "Master Craftsman Elevation",
      text: `Awarded the master's seal by unanimous vote of the district council, granting permanent voting rights on public works.`,
      kind: "achievement",
    },
    {
      age: presentAge,
      label: `Age ${presentAge}`,
      title: "Active Stewardship",
      text: `Operates as ${occupation} with established regional renown, mentoring apprentices and safeguarding civic systems.`,
      kind: "present",
    },
    {
      age: presentAge + 7,
      label: `Age ${presentAge + 7}`,
      title: "Basin Modernization",
      text: `Anticipated leadership of the watershed-wide solar and hydro cooperative integration project.`,
      kind: "future",
    },
  ];

  // 14. Earth Comparison
  // exactly 8 rows, one per EarthDimension in order:
  // Occupation, Social status, Financial circumstances, Relationships, Freedoms, Daily life, Available technology, Future opportunities.
  // divergences: 3-4 with ids "d1".."d4", each row has divergenceId referencing one.
  const earthDimensions: EarthDimension[] = [
    "Occupation",
    "Social status",
    "Financial circumstances",
    "Relationships",
    "Freedoms",
    "Daily life",
    "Available technology",
    "Future opportunities",
  ];

  const rows: EarthRow[] = earthDimensions.map((dim, idx) => {
    // Map dimensions to divergences: 0,1 -> d1; 2,3 -> d2; 4,5 -> d3; 6,7 -> d4
    const divId = idx < 2 ? "d1" : idx < 4 ? "d2" : idx < 6 ? "d3" : "d4";
    const sourceFork = divId === "d1" ? primaryFork : divId === "d2" ? pickedOthers[0] : divId === "d3" ? pickedOthers[1] : pickedOthers[2];
    const item = sourceFork.earthDimensionBecause[dim];

    return {
      dimension: dim,
      here: dim === "Occupation" ? occupation : (item?.here ?? occupation),
      earth: item?.earth ?? "Conventional contemporary equivalent",
      because: item?.because ?? "Shaped by regional divergent institutional compacts.",
      divergenceId: divId,
    };
  });

  // visitorSignals: 3-5 strings saying which of the visitor's answers shaped this life
  const resolved = ctx.resolved;
  const visitorSignals: string[] = [
    `Your declared interest in ${listJoin(resolved.interests.map((i) => cap(i)))} directed this self's craft toward ${occupation}, anchoring their labor in tangible physical materials.`,
    `Your core strength of ${listJoin(resolved.strengths.map((s) => cap(s)))} formed ${name}'s patient temperament and their instinct for de-escalating tense civic disagreements.`,
    resolved.filled.ambition
      ? `Because you left ambition open, the generator supplied a commitment to "${resolved.ambition}", which drove this self to prioritize lasting communal infrastructure over personal wealth.`
      : `Your selected ambition to "${resolved.ambition}" propelled this self to turn down distant corporate incentives to build lasting community assets at home.`,
    resolved.filled.lifePath
      ? `Because you skipped the life path choice, the generator guided this self along the walk of "${resolved.lifePath}", embedding them deeply within provincial municipal life.`
      : `Your chosen life path as "${resolved.lifePath}" directly shaped the turning point where ${name} committed their career to local river basin stewardship.`,
  ];

  return {
    engineVersion: ENGINE_VERSION,
    identity: {
      fullName: name,
      age,
      ageNote: "Standard solar calendar years measured in our physical universe.",
      personalitySummary: base.dossier.personality,
      biography: bioParas,
    },
    work: {
      occupation,
      expertise,
      careerHistory,
      responsibilities,
      workplace,
    },
    standing: {
      wealthBracket,
      wealthText: `Holds debt-free equity in the municipal workshop, verified tools, and mutualist dividend credits backed by regional trade guilds.`,
      socialClass,
      reputation: `Revered for unyielding integrity, fair pricing, and calm, unhurried competence during municipal emergencies.`,
      prestige: `High standing across regional trade guilds; frequently invited to advise district infrastructure councils.`,
      influence: `Direct voting voice in municipal resource quotas, vocational apprentice standards, and public utility planning.`,
      influenceLevel: influenceScore,
      economicCircumstances: `Thoroughly insulated against external financial speculation; daily living supported by resilient cooperative networks.`,
    },
    home: {
      dwelling,
      environment: `Temperate river valley characterized by clean water, stone bridges, and active community trade traffic.`,
      lifestyle: `Grounded physical rhythm, early mornings, shared household meals, and quiet evenings of study and music.`,
      possessions,
      transport: `Foot transit, municipal tramway, and a rugged mechanical cargo cycle for district site surveys.`,
      routine,
    },
    people,
    formative: {
      childhood: (earlyOrigin ?? "").slice(0, 180) + "...",
      education: `Rigorous municipal dual schooling combining classical natural philosophy with seven years of accredited guild apprenticeship.`,
      decisions,
      achievements,
      setbacks,
      turningPoints,
    },
    society: {
      summary: primaryFork.societySummary,
      laws: primaryFork.laws,
      culture: primaryFork.culture,
      institutions: primaryFork.institutions,
      expectations: primaryFork.expectations,
      opportunity: primaryFork.opportunity,
    },
    world: {
      technology: primaryFork.technology,
      science: primaryFork.science,
      geography: primaryFork.geography,
      history: primaryFork.history,
      environment: primaryFork.environment,
    },
    howDiffers,
    dayInLife: {
      title: "A Day in This Life",
      beats,
      closing,
    },
    futures: {
      title: "Three Possible Futures",
      paths: futures,
      disclaimer: "These forward trajectories represent speculative story branches based on current momentum, not immutable predictions.",
    },
    timeline,
    earthComparison: {
      basis: "general-reference",
      basisNote: "This Earth reference reflects typical modern conditions for comparison, because your questionnaire contained no private personal details.",
      rows,
      divergences,
      visitorSignals,
    },
  };
}
